import * as Reflux from "reflux";
import _ from "lodash";

import { SessionStore } from "../uidispatcherapi/SessionStore";
import Customer from "../datatypes/customer/Customer";
import * as TrackingUtil from "../utils/TrackingUtil";
import CustomerService from "../clients/CustomerServiceClient";

// maps from customerId -> data
let failed: {
    [customerId: string]: boolean;
} = {};
let customers: {
    [customerId: string]: Customer;
} = {};
let alreadyLoading: {
    [customerId: string]: boolean;
} = {};

/**
 * Contains data for the CURRENT and already loaded customers.
 */
const CustomerStore = Reflux.createStore({
    init() {
        this.listenTo(SessionStore, this.onSessionUpdate);
        this.onSessionUpdate();
    },

    onSessionUpdate() {
        const newCustomerId: string | null | undefined = SessionStore.getCustomerId();
        if (newCustomerId) {
            if (!alreadyLoading[newCustomerId]) {
                alreadyLoading[newCustomerId] = true;
                CustomerService.getCustomer(newCustomerId)
                    .then(response => this.onGetCustomer(response))
                    .catch(e => this.onFailed(e, newCustomerId));
            } else {
                // do nothing, as customer is currently loading or no customerId in SessionStore is set.
            }
        }
    },

    getDeliveryStoresForCurrentCustomer(): string[] {
        const customer: Customer | null | undefined = this.getCustomer();

        if (!customer) {
            return [];
        }
        return _.map(customer.deliveryAddresses, address => {
            return address.deliveryStore;
        });
    },

    isCurrentCustomerFoodCustomer(): boolean {
        const customer: Customer | null | undefined = this.getCustomer();

        return !!customer?.food;
    },

    isCurrentCustomerWebCustomer(): boolean {
        const customer: Customer | null | undefined = this.getCustomer();

        return !!customer?.validForWeb;
    },

    isCurrentCustomerFsdCustomer(): boolean {
        const customer: Customer | null | undefined = this.getCustomer();

        return !!customer?.validForFsd;
    },

    isCurrentCustomerFsdCustomerInSelectedStore() {
        if (!this.isCurrentCustomerFsdCustomer()) {
            return false;
        }
        const fsdStores = this.getDeliveryStoresForCurrentCustomer();
        const currStore = SessionStore.getCustomerStore();
        return _.includes(fsdStores, currStore);
    },

    getCustomerSegment(): string {
        const customer: Customer | null | undefined = this.getCustomer();

        const segment = customer?.segment || "";
        if (segment === "TRADER" && customer?.tags?.includes("TRADER_FRANCHISE")) {
            return "TRADER_FRANCHISE";
        }
        return segment;
    },

    onFailed(e: unknown, customerId: string): void {
        TrackingUtil.trackUIError("CustomerStore", "Load Customer failed.", e);
        failed[customerId] = true;
        this.trigger(null);
    },

    isFailed(customerId: string | null | undefined) {
        if (customerId) {
            return failed[customerId];
        } else {
            return failed[SessionStore.getCustomerId()];
        }
    },

    onGetCustomer(response: any): void {
        let customer;
        const customerId: string = response.customerId;
        try {
            customers[customerId] = new Customer(response);
            customer = customers[customerId];
            failed[customerId] = false;
            alreadyLoading[customerId] = false;
        } catch (error) {
            this.onFailed(error, customerId);
            return;
        }
        if (customer && SessionStore.getCustomerId() === customerId) {
            if (_.isNil(SessionStore.getCardholderNumber())) {
                // just in case the cardholderNumber was not set.
                SessionStore.setSelectedCustomerId(customerId, customer.primaryCardholderNumber);
            }

            const addressId: string | null | undefined = SessionStore.getSelectedDeliveryAddressId();
            if (addressId) {
                const addresses = customer.deliveryAddresses;
                const foundAddress = _.find(addresses, { integrationId: addressId });
                if (foundAddress) {
                    SessionStore.setDeliveryAddress(foundAddress);
                } else {
                    console.warn(
                        "did not find address in store and in customer, even though there was an address id: ",
                        addressId
                    );
                    SessionStore.setDeliveryAddress(null);
                }
            }
        }
        // Trigger customer without order count
        // We do that, because, in case that order service is not available, we still want to trigger the customer data.
        this.trigger(customer);
    },

    getCustomer(): Customer | null | undefined {
        return customers[SessionStore.getCustomerId()];
    },

    getCustomerStoreSelectorSettings() {
        return {
            isFsdCustomer: this.isCurrentCustomerFsdCustomer(),
            deliveryStores: this.getDeliveryStoresForCurrentCustomer()
        };
    },

    getLoggedInName() {
        const customer: Customer | null | undefined = this.getCustomer();

        return customer?.customerName;
    },

    getLoggedInCustomerDisplayNumber() {
        const customer: Customer | null | undefined = this.getCustomer();

        return customer?.displayNumber;
    },

    isLoading() {
        return (
            SessionStore.pending() || !!(SessionStore.getCustomerId() && alreadyLoading[SessionStore.getCustomerId()])
        );
    },

    // For testing
    resetStore() {
        failed = {};
        customers = {};
        alreadyLoading = {};
    }
});
export default CustomerStore;
